import * as React from 'react';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { useAuth } from 'auth';

interface FeatureFlagProviderProps {
    children: React.ReactNode;
}

/**
 * LaunchDarkly provider that initializes the SDK with user context from auth.
 * This component will show children once LaunchDarkly is initialized.
 * If no client-side ID is configured, it will render children without LaunchDarkly.
 */
export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ children }) => {
    const { user, isAuthenticated } = useAuth();
    const [Provider, setProvider] = React.useState<React.ComponentType<{
        children: React.ReactNode;
    }> | null>(null);
    const [isInitialized, setIsInitialized] = React.useState(false);
    const [initError, setInitError] = React.useState<Error | null>(null);

    React.useEffect(() => {
        const clientSideID = process.env.REACT_APP_LAUNCHDARKLY_CLIENT_ID;

        // If no client ID is configured, skip LaunchDarkly initialization
        if (!clientSideID) {
            setIsInitialized(true);
            return;
        }

        if (!isAuthenticated || !user) {
            setProvider(null);
            return;
        }

        const initLaunchDarkly = async () => {
            try {
                // Set up user context for LaunchDarkly
                const context = {
                    kind: 'customer',
                    key: user.customer_id?.toString(),
                };

                const provider = await asyncWithLDProvider({
                    clientSideID,
                    context,
                    options: {
                        // Enable streaming for real-time flag updates
                        streaming: true,
                        // Bootstrap from localStorage for faster initialization
                        bootstrap: 'localStorage',
                    },
                });

                setProvider(() => provider);
                setIsInitialized(true);
            } catch (error) {
                setInitError(error as Error);
                setIsInitialized(true);
            }
        };

        initLaunchDarkly();
    }, [user, isAuthenticated]);

    if (!isInitialized) {
        return <>{children}</>;
    }

    if (initError || !Provider) {
        return <>{children}</>;
    }

    // Render children wrapped with the feature flag provider
    return <Provider>{children}</Provider>;
};
